import { Coordinates } from '../Mapbox'
import { useTranslation } from '../Translation'
import { PrefectureIndex } from '../Util'
import { useJapaneseArtisanArticles } from './useJapaneseArtisanArticles'
import { useEnglishArtisanArticles } from './useEnglishArtisanArticles'

export type Artisan = ArtisanPartFromMicrocms & ArtisanPartFromMapbox

type Prefecture = keyof typeof PrefectureIndex
export type ArtisanPartFromMicrocms = {
  id: string
  workshop: string
  workshopEn: string
  prefectureIndex: number
  prefecture: Prefecture
  city: string
  blogIds: string[]
}

export type ArtisanPartFromMapbox = {
  coordinates: Coordinates
  prefectureEn: string
  cityEn: string
}

export type RawArtisanPartFromMicrocms = {
  id: string
  workshop: string
  workshopEn: string
  location: string
  blogIds: { id: string }[]
}

export const convertToArtisanPartFromMicrocms = (
  rawData: RawArtisanPartFromMicrocms,
): ArtisanPartFromMicrocms => {
  // 既に半角 or 全角スペースが入っていても対応できるように設定
  const regex = /(.{2,3}[都道府県])[\u{20}\u{3000}]?/u
  const prefecture = rawData.location.match(regex)?.[1] as
    | Prefecture
    | undefined
  const city = rawData.location.replace(regex, '')
  const prefectureIndex =
    PrefectureIndex[prefecture as keyof typeof PrefectureIndex]

  if (prefecture === undefined || !prefectureIndex) {
    throw new Error(`都道府県名が不正です: ${prefecture}`)
  }
  return {
    ...rawData,
    prefectureIndex,
    prefecture,
    city,
    blogIds: rawData.blogIds.map(blogId => blogId.id),
  }
}

export const useArtisanArticles = () => {
  const { locale } = useTranslation()
  const japanesseArticles = useJapaneseArtisanArticles()
  const englishArticles = useEnglishArtisanArticles()

  return locale === 'en' ? englishArticles : japanesseArticles
}

type ArtisanFeature = {
  id: string
  name: string
  nameEn: string
  message: string
  title: string
  titleEn: string
  profile: string
  articleIds: string[]
}
export const getArtisanFeatures = (): ArtisanFeature[] => [
  {
    id: '001',
    name: '中川 衛',
    nameEn: 'Mamoru Nakagawa',
    message: '電機メーカーの社員から、<br />伝統工芸の頂点へ。',
    title: '人間国宝・重要無形文化財「彫金」保持者',
    titleEn:
      'Living National Treasure<br />Important Intangible Cultural Property “Metal Engraving” Preserver',
    profile: '/assets/artisan/feature001.jpg',
    articleIds: [
      'mamoru_nakagawa1',
      'mamoru_nakagawa2',
      'mamoru_nakagawa3',
      'mamoru_nakagawa4',
      'mamoru_nakagawa5',
    ],
  },
]

export type ArtisanSeriesId = '001' | '002' | '003'
type ArtisanSeries = {
  id: ArtisanSeriesId
  title: string
  titleEn: string
  subTitle: string
  subTitleEn: string
  description: string
  descriptionEn: string
  articleIds: string[]
}
export const getArtisanSeriesList = (): ArtisanSeries[] => [
  {
    id: '001',
    title: '工芸の未来地図',
    titleEn: 'The Future Map of Crafts',
    subTitle: '次世代の工芸家たちが見つめる“これから”',
    subTitleEn: 'The Next Generation of Craftsmen Look Toward the Future',
    description:
      '伝統を学び、自分らしい表現を模索する若き作り手たち。 迷いながらも進むその姿には、未来のヒントがあります。 継ぐだけでは終わらない、次の工芸を描く若者たちの声を届けます。',
    descriptionEn:
      'Young artisans who are learning tradition and searching for their own unique expressions. Their journey, filled with uncertainty yet moving forward, holds clues to the future. We bring you the voices of these young people who are not content to simply carry on tradition, but are creating the next generation of crafts.',
    articleIds: ['momoka_ienaga'],
  },
  {
    id: '002',
    title: 'Crossing Perspectives',
    titleEn: 'Crossing Perspectives',
    subTitle: '多様な文化と価値観が交錯する、日本の工芸',
    subTitleEn: 'Japanese Craft at the Intersection of Cultures and Values',
    description:
      'なぜ彼らは、日本の工芸に惹かれたのか？暮らし、働き、学びのなかで出会った“技”の世界。異なる文化に育まれた感性で見つめた、日本の手仕事の魅力と、その先に広がる可能性を語ります。',
    descriptionEn:
      'What draws them to Japanese craftsmanship? Through living, working, and learning in Japan, they encountered a world of refined skills and artistry. From their unique cultural backgrounds, they share how Japanese handcrafts resonate with them—and the possibilities they see beyond.',
    articleIds: [
      'jonathan_barnabe',
      'steve_beimel',
      'eric_chevallier',
      'tulika_ranjan',
      'vera_bracklo',
      'mathilde_roseanne_bregeon',
      'john_oglevee',
      'emilie_even',
      'allan_west',
      'clementine_sandner',
      'sally_hancox',
      'david_stanley_hewett',
      'rudge_jeff',
    ],
  },
  {
    id: '003',
    title: '伝統のそばで暮らす',
    titleEn: 'Living Beside Tradition',
    subTitle: '“職人の妻”として生きる選択と日常',
    subTitleEn: "The Choice and Daily Life of a ”Craftsman's Wife”",
    description:
      '伝統を受け継ぐ家に嫁いだ女性たち。 職人の家業、地域との関係、子育てとの両立…… 華やかには語られない“もう一つの現場”から見える、工芸のリアルと希望を描きます。',
    descriptionEn:
      "Women who married into families that carry on traditional crafts. Balancing the artisan's family business, community ties, and raising children... This series portrays the reality and hope of craftsmanship from the “other side of the story” that is rarely spoken of.",
    articleIds: ['sayaka_ikeda'],
  },
]
